/**
 * Populate settings modal with current state values.
 */
import {
  sanitizePercentage,
  sanitizeCurrency,
  formatPercentageInput,
  formatCurrencyInput,
} from '../../modules/formatting';
import { normalizeHomeZipPostal } from '../../modules/utils';
import { ensureSettings } from '../../modules/state';
import { populateTimeZones, maybeShowFirstRunModal } from '../../settingsController';
import type { SettingsElements, SettingsHandlerDeps } from './types';

function setupUnitsHintRefresh(els: SettingsElements): void {
  function refreshUnitsHint() {
    if (!els.settingsUnitsAutoIndicator) return;

    const sel = els.settingsDistanceUnits ? els.settingsDistanceUnits.value || 'auto' : 'auto';

    if (sel !== 'auto') {
      els.settingsUnitsAutoIndicator.dataset.visible = 'false';
      return;
    }

    const v = els.settingsZip ? els.settingsZip.value || '' : '';
    const isCA = /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i.test(v.trim());
    els.settingsUnitsAutoIndicator.textContent = isCA ? 'Auto: km' : 'Auto: mi';
    els.settingsUnitsAutoIndicator.dataset.visible = 'true';
  }

  refreshUnitsHint();

  if (els.settingsZip) {
    els.settingsZip.addEventListener('input', refreshUnitsHint);
  }
  if (els.settingsDistanceUnits) {
    els.settingsDistanceUnits.addEventListener('change', refreshUnitsHint);
  }
}

export async function populateSettingsModal(deps: SettingsHandlerDeps): Promise<void> {
  const { state, els, cfg, getUserProfile } = deps;

  populateTimeZones(state);
  const settings = ensureSettings();

  if (els.settingsZip) {
    const currentZip = settings?.zipCode || '';
    els.settingsZip.value = currentZip;
  }

  if (els.settingsDistanceUnits) {
    let du = settings?.distanceUnits || 'auto';
    if (du !== 'mi' && du !== 'km') {
      du = 'auto';
    }
    els.settingsDistanceUnits.value = du;
  }

  setupUnitsHintRefresh(els);

  if (els.settingsTaxRate) {
    const currentTax = sanitizePercentage(settings?.taxRate, 0);
    els.settingsTaxRate.value = formatPercentageInput(currentTax);
  }

  if (els.settingsBidderPremium) {
    const currentPremium = sanitizePercentage(settings?.bidderPremium, 15);
    els.settingsBidderPremium.value = formatPercentageInput(currentPremium);
  }

  if (els.settingsSellerCosts) {
    const currentSeller = sanitizePercentage(settings?.sellerCosts, 14);
    els.settingsSellerCosts.value = formatPercentageInput(currentSeller);
  }

  if (els.settingsFixedLotCost) {
    const currentLotCost = sanitizeCurrency(settings?.fixedLotCost, 0);
    els.settingsFixedLotCost.value = formatCurrencyInput(currentLotCost);
  }

  if (els.settingsEnableNotifications) {
    els.settingsEnableNotifications.checked = !!settings.enableNotifications;
  }

  if (els.settingsNotifyLeadMinutes) {
    const leadList: number[] = Array.isArray(
      (settings as Record<string, unknown>).notifyLeadMinutes
    )
      ? ((settings as Record<string, unknown>).notifyLeadMinutes as number[])
          .filter((m: number) => Number.isFinite(m))
          .map(Number)
      : [60, 30, 10];
    els.settingsNotifyLeadMinutes.value = leadList.join(',');
  }

  if (els.settingsNotificationsPermissionStatus && typeof Notification !== 'undefined') {
    els.settingsNotificationsPermissionStatus.textContent = Notification.permission;
  }

  try {
    if (els.profileCompany) {
      const prof = await (typeof getUserProfile === 'function'
        ? getUserProfile(cfg)
        : Promise.resolve(null));

      if (prof) {
        if (els.profileCompany && typeof prof.company === 'string') {
          els.profileCompany.value = prof.company;
        }
        if (els.settingsDistanceUnits && typeof prof.distanceUnits === 'string') {
          const du = prof.distanceUnits.toLowerCase();
          if (du === 'mi' || du === 'km' || du === 'auto') {
            els.settingsDistanceUnits.value = du;
          }
        }
        maybeShowFirstRunModal(prof);
      }
    }
  } catch {
    // Silently fail on profile load
  }

  // Note: Options is now a separate page, no need to toggle card visibility
}
